import { Component, computed, DestroyRef, input, output, signal, ViewChild } from '@angular/core';
import { ConfirmationModalComponent } from "../../../../parts/confirmation-modal/confirmation-modal.component";
import { PropertyForm } from "../../../../forms/property/property.form/property.form";
import { InfoModalComponent } from "../../../../parts/info-modal/info-modal.component";
import { AddressVerificationModalComponent } from "../../../../parts/address-verification-modal/address-verification-modal.component";
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { PropertyModel } from '../../../../core/models/data/property.model';
import { AddressVerificationPrompt } from '../../../../core/models/ui/address-verification-prompt.model';
import { AddressVerificationChoice, ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { FormState } from '../../../../core/enums/forms.enums';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { AddressVerificationMessagingConstants, GenericMessagingConstants, PropertiesMessagingConstants } from '../../../../core/constants/messaging.constants';
import { PropertyUtilities } from '../../../../core/utilities/property.utilities';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { FeatureFlag } from '../../../../core/enums/feature-flags.enums';

@Component({
  selector: 'ms-property-form-modal',
  imports: [ConfirmationModalComponent, PropertyForm, InfoModalComponent, AddressVerificationModalComponent, EscCloseDirective],
  templateUrl: './property-form-modal.component.html'
})
export class PropertyFormModalComponent extends BaseFormComponent implements CanComponentDeactivate {
  private resetFormOnConfirm : boolean = true;
  public organization = input<OrganizationModel | undefined | null>(null);
  public property = input<PropertyModel | undefined>(undefined);
  public formState = input<FormState>(FormState.Create);
  public onSuccessConfirm = output();

  public FormState = FormState;

  /** The verification awaiting the user's answer. Cleared once they answer or the submit finishes. */
  public addressVerificationPrompt = signal<AddressVerificationPrompt | undefined>(undefined);

  // Render gate, not just a behaviour gate: feature flags are how features reach production, so an
  // organization without this one must see no trace of it — including dialog copy sitting unused in
  // the DOM. PropertyForm applies the same flag before it would ever ask for these dialogs.
  public isAddressVerificationEnabled = computed<boolean>(() =>
    this.userSessionService.isFeatureEnabled(FeatureFlag.AddressVerification));

  @ViewChild(PropertyForm) propertyFormRef!: PropertyForm;
  @ViewChild('addPropertyInfoModalRef') addPropertyInfoModalRef!: InfoModalComponent;
  @ViewChild('addPropertyConfirmationModalRef') addPropertyConfirmationModalRef!: InfoModalComponent;
  @ViewChild('editPropertyConfirmationModalRef') editPropertyConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('addressVerificationUnavailableModalRef') addressVerificationUnavailableModalRef!: ConfirmationModalComponent;

  constructor(
    private bootstrapService: BootstrapService,
    private dirtyFormRegistry: DirtyFormRegistryService,
    private userSessionService: UserSessionService,
    private destroyRef: DestroyRef
  ) {
    super();
    this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));
  }

  public onCancel(): void {
    if (this.propertyFormRef.propertyForm.dirty) {
      this.promptUnsavedChanges();
      this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Hide);
    } else {
      this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Hide);
      this.dirtyFormRegistry.unregister(this);
    }
  }

  private promptUnsavedChanges(): void {
    this.addPropertyConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.addPropertyConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
    this.bootstrapService.manageModal("#addPropertyConfirmationModal", ModalActions.Show);
  }

  public canDeactivate(): boolean | Promise<boolean> {
    return this.awaitLeaveConfirmation(this.propertyFormRef.propertyForm.dirty && !this.submitSucceeded, () => {
      this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Hide);
      this.promptUnsavedChanges();
    });
  }

  public openPropertyModal(): void {
    this.submitSucceeded = false;
    this.dirtyFormRegistry.register(this);
    this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Show);

    if (this.formState() === FormState.Update) {
      this.propertyFormRef.populateFormForEdit();
    }
  }

  public confirmationModalConfirmed(): void {
    this.bootstrapService.manageModal("#addPropertyConfirmationModal", ModalActions.Hide);
    this.propertyFormRef.resetForm();
    this.dirtyFormRegistry.unregister(this);
    this.resolvePendingLeave(true);
  }

  public infoModalConfirmed() : void {
    if (this.resetFormOnConfirm === true) {
      this.submitSucceeded = false;
      this.propertyFormRef.resetForm();
      this.dirtyFormRegistry.unregister(this);
      this.onSuccessConfirm.emit();
    } else {
      this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Show);
    }
  }

  //region Address verification

  public onAddressVerificationRequested(prompt: AddressVerificationPrompt): void {
    this.addressVerificationPrompt.set(prompt);
    this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#addressVerificationModal", ModalActions.Show);
  }

  public async addressVerificationConfirmed(choice: AddressVerificationChoice): Promise<void> {
    this.bootstrapService.manageModal("#addressVerificationModal", ModalActions.Hide);

    if (choice === AddressVerificationChoice.Standardized) {
      await this.propertyFormRef.acceptVerifiedAddress();
    } else {
      await this.propertyFormRef.keepEnteredAddress();
    }
  }

  // Edit is a cancel: the entry is still on the form behind this dialog, and submitting it again
  // is a fresh verification (free, because the provider's answer for it is cached).
  //
  // The prompt is deliberately left set. Clearing it here would empty the dialog while it is still
  // on screen fading out, and nothing reads it again — the next verification overwrites it before
  // the dialog is shown.
  public addressVerificationEditRequested(): void {
    this.propertyFormRef.cancelVerification();
    this.bootstrapService.manageModal("#addressVerificationModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Show);
  }

  public onAddressVerificationUnavailable(): void {
    this.addressVerificationUnavailableModalRef.modalType.set(ModalTypes.Exclamation);
    this.addressVerificationUnavailableModalRef.message.set(AddressVerificationMessagingConstants.UNAVAILABLE_CONFIRMATION());

    this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#addressVerificationUnavailableModal", ModalActions.Show);
  }

  public async addressVerificationUnavailableConfirmed(): Promise<void> {
    this.bootstrapService.manageModal("#addressVerificationUnavailableModal", ModalActions.Hide);
    await this.propertyFormRef.keepEnteredAddress();
  }

  public addressVerificationUnavailableCancelled(): void {
    this.propertyFormRef.cancelVerification();
    this.bootstrapService.manageModal("#addressVerificationUnavailableModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Show);
  }

  // The provider refused the address outright, so there is nothing to save: the info modal's
  // confirm returns the user to the form with their entry intact.
  public onAddressVerificationRejected(message: string): void {
    this.resetFormOnConfirm = false;
    this.addPropertyInfoModalRef.modalType.set(ModalTypes.Failure);
    this.addPropertyInfoModalRef.message.set(message);

    this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#addPropertyInfoModal", ModalActions.Show);
  }

  //endregion Address verification

  public onPropertyResult(result: ServiceResultModel): void {
    this.addressVerificationPrompt.set(undefined);
    this.resetFormOnConfirm = result.success!;
    if (result.success === true) {
      this.submitSucceeded = true;
    }

    this.addPropertyInfoModalRef.message.set(result?.message);

    if (result.success === true) {
      this.addPropertyInfoModalRef.modalType.set(ModalTypes.Success);
    } else {
      this.addPropertyInfoModalRef.modalType.set(ModalTypes.Failure);
    }

    this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#addPropertyInfoModal", ModalActions.Show);
  }

  public confirmationModalCancelled(): void {
    this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Show);
    this.bootstrapService.manageModal("#addPropertyConfirmationModal", ModalActions.Hide);
    this.resolvePendingLeave(false);
  }

  public onEditConfirmationRequested(): void {
    const propertyData = this.property();
    const propertyLabel = propertyData ? PropertyUtilities.getPropertyLabel(propertyData) : '';
    const message = PropertiesMessagingConstants.PROPERTIES_EDIT_PROPERTY_CONFIRMATION()
      .replace('{propertyLabel}', propertyLabel);

    this.editPropertyConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.editPropertyConfirmationModalRef.message.set(message);

    this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#editPropertyConfirmationModal", ModalActions.Show);
  }

  public async editConfirmationModalConfirmed(): Promise<void> {
    this.bootstrapService.manageModal("#editPropertyConfirmationModal", ModalActions.Hide);
    await this.propertyFormRef.beginSubmit();
  }

  public editConfirmationModalCancelled(): void {
    this.bootstrapService.manageModal("#editPropertyConfirmationModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#propertyFormModal", ModalActions.Show);
  }

}
